import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Spinner } from '../ui/spinner';
import { Alert, AlertDescription } from '../ui/alert';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../ui/table';
import { AlertCircle, Check, Pencil, Plus, Trash2, X } from 'lucide-react';
import { crmCampaignsAPI } from '../../services/api';
import {
  CollapsibleSection, DeleteDialog, FieldLabel, GamesJsonEditor,
  apiError, isValidAmount, isValidCurrency,
} from './campaignShared';

// Wednesday has two games rows. Priority 1 can be switched off, which the backend records by
// renaming its campaign to wednesday_freespins_NOT_IN_USE.
const PRIORITIES = [1, 2];
const TOGGLEABLE_PRIORITY = 1;

// Shown for an empty value: a tier a currency doesn't offer.
const NO_LIMIT = '/';
// `editing` value while a new currency is being added.
const NEW_ROW = Symbol('new');

// Active/Inactive switch for priority 1. It saves straight away; the backend logs each switch.
function ActiveSwitch({ row, onChange }) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const set = async (active) => {
    if (active === row.active) return;
    setSaving(true);
    setError('');
    try {
      await onChange(active);
    } catch (err) {
      setError(apiError(err, 'Failed to switch.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex items-center gap-2">
      <div className="inline-flex rounded-lg border border-white/10 p-0.5" role="group" aria-label="Priority 1 status">
        {[{ value: true, label: 'Active' }, { value: false, label: 'Inactive' }].map((o) => (
          <Button key={o.label} size="sm" variant={row.active === o.value ? 'default' : 'ghost'}
            className="h-7 px-3 text-xs" aria-pressed={row.active === o.value}
            disabled={saving} onClick={() => set(o.value)}>
            {o.label}
          </Button>
        ))}
      </div>
      {saving && <Spinner size="sm" />}
      {error && <span className="text-xs font-medium text-negative">{error}</span>}
    </div>
  );
}

function GamesSection() {
  // Rows by priority; a priority without a row yet is missing.
  const [rows, setRows] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const put = (row) => setRows((r) => ({ ...r, [row.priority]: row }));

  useEffect(() => {
    (async () => {
      try {
        const res = await crmCampaignsAPI.listWednesdayGames();
        if (!Array.isArray(res.data?.games)) throw new Error('unexpected response');
        setRows(Object.fromEntries(res.data.games.map((g) => [g.priority, g])));
      } catch (err) {
        setError(apiError(err, 'Failed to load games.'));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const saveGames = (priority) => async (games) => {
    const res = await crmCampaignsAPI.setWednesdayGames(priority, games);
    put(res.data);
  };

  const setActive = async (active) => {
    const res = await crmCampaignsAPI.setWednesdayGamesActive(TOGGLEABLE_PRIORITY, active);
    put(res.data);
  };

  return (
    <CollapsibleSection title="Games">
      {error && <Alert variant="error"><AlertDescription>{error}</AlertDescription></Alert>}
      {loading ? (
        <Card><CardContent className="flex justify-center py-6"><Spinner /></CardContent></Card>
      ) : !error && PRIORITIES.map((priority) => {
        const row = rows[priority];
        return (
          <Card key={priority}>
            <CardContent className="p-5 space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <FieldLabel>Priority {priority}</FieldLabel>
                {priority === TOGGLEABLE_PRIORITY && (row
                  ? <ActiveSwitch row={row} onChange={setActive} />
                  : <span className="text-xs font-medium text-ink-mute">Save games to set active/inactive.</span>)}
              </div>
              <GamesJsonEditor value={row?.games_text ?? ''} onSave={saveGames(priority)} />
            </CardContent>
          </Card>
        );
      })}
    </CollapsibleSection>
  );
}

// Each deposit limits table (Wednesday's, the weekly one) holds one row per range, but the page
// edits it one currency at a time: one "deposit from" per freespin tier (the tiers come from the
// backend). The backend turns that into rows, each reaching up to the next tier's amount and the
// top one open-ended. A tier left empty is not offered for that currency.
const emptyTiers = (n) => Array(n).fill('');
const tierInputs = (row, n) => (row ? row.deposit_from.map((v) => v ?? '') : emptyTiers(n));

// '' when the inputs can be saved, otherwise why not. Mirrors the backend's rules; the backend
// compares exactly, this is just a hint.
const tiersError = (inputs) => {
  const set = inputs.map((v) => v.trim()).filter((v) => v !== '');
  if (set.length === 0) return 'Enter at least one amount.';
  if (!set.every(isValidAmount)) return 'Amounts must be non-negative numbers.';
  if (set.some((v, i) => i > 0 && Number(v) <= Number(set[i - 1]))) return 'Amounts must rise with the freespin count.';
  return '';
};

function TierRow({ api, tiers, row, isNew, onSaved, onCancel, onEdit, onDelete, editing }) {
  const [currency, setCurrency] = useState('');
  const [inputs, setInputs] = useState(() => tierInputs(row, tiers.length));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!editing) return;
    setCurrency('');
    setInputs(tierInputs(row, tiers.length));
    setError('');
  }, [editing, row, tiers.length]);

  const validation = tiersError(inputs);
  const valid = !validation && (!isNew || isValidCurrency(currency));

  const save = async () => {
    setSaving(true);
    setError('');
    const body = { deposit_from: inputs.map((v) => (v.trim() === '' ? null : v.trim())) };
    try {
      if (isNew) {
        await api.create({ ...body, currency: currency.trim() });
      } else {
        await api.update(row.currency, body);
      }
      onSaved();
    } catch (err) {
      setError(apiError(err, 'Failed to save limits.'));
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <TableRow>
        <TableCell className="font-mono text-xs font-bold">
          <span className="inline-flex items-center gap-1.5">
            {row.currency}
            {row.irregular && (
              <span title="The stored ranges don't follow the tiers. Saving this currency rewrites them.">
                <AlertCircle className="h-3.5 w-3.5 text-warning" aria-label="Irregular ranges" />
              </span>
            )}
          </span>
        </TableCell>
        {row.deposit_from.map((v, i) => (
          <TableCell key={tiers[i]} className="text-right font-mono text-xs">{v ?? NO_LIMIT}</TableCell>
        ))}
        <TableCell className="text-right whitespace-nowrap">
          <Button variant="ghost" size="sm" onClick={onEdit} aria-label="Edit">
            <Pencil className="h-3.5 w-3.5" />
          </Button>
          {onDelete && (
            <Button variant="ghost" size="sm" onClick={onDelete} aria-label="Delete">
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          )}
        </TableCell>
      </TableRow>
    );
  }

  const hint = error || (inputs.some((v) => v.trim() !== '') ? validation : '');
  return (
    <>
      <TableRow>
        <TableCell className="font-mono text-xs font-bold">
          {isNew ? (
            <Input className="h-8 w-24 text-xs" value={currency} placeholder="e.g. EUR" autoFocus
              onChange={(e) => setCurrency(e.target.value)} aria-label="Currency" />
          ) : row.currency}
        </TableCell>
        {inputs.map((v, i) => (
          <TableCell key={tiers[i]} className="text-right">
            <Input className="h-8 w-28 ml-auto text-right text-xs font-mono" inputMode="decimal" value={v}
              placeholder={NO_LIMIT} aria-label={`Deposit from for ${tiers[i]} FS`}
              autoFocus={!isNew && i === 0}
              onChange={(e) => setInputs((xs) => xs.map((x, j) => (j === i ? e.target.value : x)))}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && valid && !saving) save();
                if (e.key === 'Escape') onCancel();
              }} />
          </TableCell>
        ))}
        <TableCell className="text-right whitespace-nowrap">
          <Button variant="ghost" size="sm" onClick={save} disabled={!valid || saving} aria-label="Save">
            {saving ? <Spinner size="sm" /> : <Check className="h-3.5 w-3.5" />}
          </Button>
          <Button variant="ghost" size="sm" onClick={onCancel} disabled={saving} aria-label="Cancel">
            <X className="h-3.5 w-3.5" />
          </Button>
        </TableCell>
      </TableRow>
      {(hint || row?.irregular) && (
        <TableRow>
          <TableCell colSpan={tiers.length + 2} className="pt-0 text-xs font-medium">
            {hint
              ? <span className="text-negative">{hint}</span>
              : <span className="text-ink-dim">The stored ranges don't follow the tiers; saving rewrites them.</span>}
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

// A tier-edited deposit limits table. `api` holds its calls: list, update(currency, body), and
// optionally create(body) and remove(currency) — without them there is no adding or deleting
// currencies (the weekly limits have just their one, which the backend always lists).
function TierLimitsSection({ title, description, defaultCollapsed, api }) {
  const [view, setView] = useState({ tiers: [], currencies: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // The currency being edited, or NEW_ROW while adding one.
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.list();
      if (!Array.isArray(res.data?.tiers) || !Array.isArray(res.data?.currencies)) throw new Error('unexpected response');
      setView(res.data);
    } catch (err) {
      setError(apiError(err, 'Failed to load limits.'));
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const saved = () => { setEditing(null); load(); };
  const { tiers, currencies } = view;

  return (
    <CollapsibleSection title={title} description={description} defaultCollapsed={defaultCollapsed} actions={api.create && (
      <Button size="sm" onClick={() => setEditing(NEW_ROW)} disabled={loading || editing === NEW_ROW}>
        <Plus className="h-3.5 w-3.5 mr-1.5" /> Add currency
      </Button>
    )}>
      {error && <Alert variant="error"><AlertDescription>{error}</AlertDescription></Alert>}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-10"><Spinner /></div>
          ) : currencies.length === 0 && editing !== NEW_ROW ? (
            <p className="py-10 text-center text-sm font-medium text-ink-dim">No deposit limits yet.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Currency</TableHead>
                  {tiers.map((t) => (
                    <TableHead key={t} className="text-right" title={`Deposit from which ${t} freespins are given`}>
                      {t} FS
                    </TableHead>
                  ))}
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {editing === NEW_ROW && (
                  <TierRow api={api} tiers={tiers} isNew editing onSaved={saved} onCancel={() => setEditing(null)} />
                )}
                {currencies.map((row) => (
                  <TierRow key={row.currency} api={api} tiers={tiers} row={row}
                    editing={editing === row.currency}
                    onEdit={() => setEditing(row.currency)} onCancel={() => setEditing(null)}
                    onSaved={saved} onDelete={api.remove && (() => setDeleting(row))} />
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
      {api.remove && (
        <DeleteDialog
          open={Boolean(deleting)} title="Delete currency?"
          description={deleting ? `All ${deleting.currency} deposit limits.` : ''}
          onConfirm={() => api.remove(deleting.currency)}
          onClose={() => setDeleting(null)}
          onDeleted={() => { setDeleting(null); load(); }}
        />
      )}
    </CollapsibleSection>
  );
}

// Module-level so their identity is stable across renders (TierLimitsSection loads on change).
const WEDNESDAY_LIMITS_API = {
  list: crmCampaignsAPI.listWednesdayLimits,
  create: crmCampaignsAPI.createWednesdayLimits,
  update: crmCampaignsAPI.updateWednesdayLimits,
  remove: crmCampaignsAPI.deleteWednesdayLimits,
};
// USD only, always listed; a save creates its rows the first time.
const WEEKLY_LIMITS_API = {
  list: crmCampaignsAPI.listWeeklyLimits,
  update: (_currency, body) => crmCampaignsAPI.setWeeklyLimits(body),
};

const WednesdayFreespinsPanel = () => (
  <div className="space-y-6">
    <GamesSection />
    <TierLimitsSection title="Deposit limits" api={WEDNESDAY_LIMITS_API} />
    <TierLimitsSection defaultCollapsed title="Weekly Freespins Deposit Limits"
      description="Player's total deposit amount during the previous 45 days" api={WEEKLY_LIMITS_API} />
  </div>
);

export default WednesdayFreespinsPanel;
